// src/components/Trading/TradingModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { tradingService } from '../../services/tradingService';
import { portfolioService } from '../../services/portfolioService';
import { copyTradingService } from '../../services/copyTradingService';
import { useCurrency } from '../../context/CurrencyContext';
import { useSellLockRetry } from '../../hooks/useSellLockRetry';
import './TradingModal.css';

// ── Fee constants (mirror the backend exactly) ────────────────────────────────
const BUY_FEE_RATE  = 0.005;   // 0.5%
const SELL_FEE_RATE = 0.010;   // 1.0%

/**
 * Calculate fee and totals from a subtotal.
 * All arithmetic uses the same rounding the backend uses (2dp, half-up).
 */
function computeFeeBreakdown(orderSide, subtotal) {
  if (!subtotal || subtotal <= 0) {
    return { subtotal: 0, fee: 0, feeRate: 0, grandTotal: 0 };
  }
  const rate      = orderSide === 'BUY' ? BUY_FEE_RATE : SELL_FEE_RATE;
  const fee       = Math.round(subtotal * rate * 100) / 100;   // round to 2dp
  const grandTotal = orderSide === 'BUY'
    ? Math.round((subtotal + fee) * 100) / 100
    : Math.round((subtotal - fee) * 100) / 100;
  return { subtotal, fee, feeRate: rate * 100, grandTotal };
}


const TradingModal = ({ stock, orderType, onClose }) => {
  const { currency, formatCurrency, getCurrencySymbol } = useCurrency();

  const [orderSide,     setOrderSide]     = useState(orderType.includes('buy') ? 'BUY' : 'SELL');
  const [isMarketOrder, setIsMarketOrder] = useState(orderType.includes('market'));
  const [quantity,      setQuantity]      = useState('');
  const [totalPrice,    setTotalPrice]    = useState('');
  const [activeField,   setActiveField]   = useState(null);   // 'quantity' | 'total'
  const [limitPrice,    setLimitPrice]    = useState('');
  const [useReferralBonus, setUseReferralBonus] = useState(false);

  const [portfolioSummary,    setPortfolioSummary]    = useState(null);
  const [userHolding,         setUserHolding]         = useState(null);
  const [referralWallet,      setReferralWallet]      = useState(null);
  const [pendingOrders,       setPendingOrders]       = useState([]);
  const [recentTransactions,  setRecentTransactions]  = useState([]);

  const [showPendingOrders,  setShowPendingOrders]  = useState(false);
  const [showTransactions,   setShowTransactions]   = useState(false);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(null);

  // ── Sell-lock retry (shared hook) ────────────────────────────────────────────
  // Handles the spinner-keep-going + silent retry pattern when admin locks sell.
  // Automatically cancels any pending retry when the modal unmounts.
  const { loading: sellLoading, attemptAction } = useSellLockRetry();

  // Combine: show spinner either from normal loading OR from sell-lock retry
  const isLoading = loading || sellLoading;

  // ── Copy trading state ───────────────────────────────────────────────────────
  // leaderProfile: null = not a leader, {} = is a leader (has profile)
  const [leaderProfile,     setLeaderProfile]     = useState(null);
  const [copyTradeEnabled,  setCopyTradeEnabled]  = useState(false);
  const [copyTradeAmount,   setCopyTradeAmount]   = useState('');
  const [eligibleInfo,      setEligibleInfo]      = useState(null);   // { eligible, total, amount_display }
  const [eligibleLoading,   setEligibleLoading]   = useState(false);
  const [copyTradeChecked,  setCopyTradeChecked]  = useState(false);  // profile fetch done

  // ── Current price (already in user's display currency from backend) ─────────
  const getCurrentStockPrice = () => {
    if (currency === 'USD') {
      if (stock.current_price_display) {
        const n = parseFloat(stock.current_price_display.replace(/[^0-9.-]+/g, ''));
        if (!isNaN(n)) return n;
      }
      return parseFloat(stock.current_price) || 0;
    }
    return parseFloat(stock.current_price) || 0;
  };

  // ── Fetch user data ──────────────────────────────────────────────────────────
  useEffect(() => { fetchUserData(); }, [stock.symbol]);

  useEffect(() => {
    const handler = () => fetchUserData();
    window.addEventListener('currencyChange', handler);
    return () => window.removeEventListener('currencyChange', handler);
  }, []);

  const fetchUserData = async () => {
    try {
      const summary = await tradingService.getPortfolioSummary();
      setPortfolioSummary(summary);

      const referralData = await portfolioService.getReferralWallet();
      setReferralWallet(referralData);

      try {
        const holding = await tradingService.getUserStockHolding(stock.symbol);
        setUserHolding(holding);
      } catch {
        setUserHolding(null);
      }

      const allOrders  = await tradingService.getUserOrders({ status: 'PENDING' });
      const stockOrders = allOrders.filter(o => o.stock.symbol === stock.symbol);
      setPendingOrders(stockOrders);

      const transactions = await tradingService.getTransactions({ stock_id: stock.id });
      setRecentTransactions(transactions.slice(0, 5));
    } catch (err) {
      console.error('❌ Error fetching user data:', err);
    }
  };

  // ── Fetch leader profile (only once per modal open) ──────────────────────────
  // Shown for both BUY and SELL orders — leaders can mirror sells too
  useEffect(() => {
    if (copyTradeChecked) return;
    fetchLeaderProfile();
  }, [copyTradeChecked]);

  const fetchLeaderProfile = async () => {
    try {
      const profile = await copyTradingService.getMyProfile();
      // Only show copy trade section if:
      // 1. User has an active copy trading profile
      // 2. They have at least 1 active follower
      if (profile && profile.is_active && profile.active_followers_count > 0) {
        setLeaderProfile(profile);
      } else {
        setLeaderProfile(null);
      }
    } catch {
      setLeaderProfile(null);
    } finally {
      setCopyTradeChecked(true);
    }
  };

  // ── Fetch eligible follower count (debounced) ─────────────────────────────────
  // For BUY: checks balance for fixed amount
  // For SELL: checks who actually holds the stock (ignores amount)
  useEffect(() => {
    if (!copyTradeEnabled || !leaderProfile) {
      setEligibleInfo(null);
      return;
    }

    // For SELL orders, we don't need amount to check eligibility - just check who holds the stock
    if (orderSide === 'SELL') {
      fetchEligibleCountForSell();
      return;
    }

    // For BUY orders, need amount
    const amount = parseFloat(copyTradeAmount);
    if (!amount || amount <= 0) {
      setEligibleInfo(null);
      return;
    }

    // Debounce — wait 600ms after user stops typing
    const timer = setTimeout(() => {
      fetchEligibleCountForBuy(amount);
    }, 600);
    return () => clearTimeout(timer);
  }, [copyTradeAmount, copyTradeEnabled, leaderProfile, currency, orderSide]);

  const fetchEligibleCountForBuy = async (amount) => {
    setEligibleLoading(true);
    try {
      const data = await copyTradingService.getEligibleFollowers(amount, stock.id);
      setEligibleInfo(data);
    } catch {
      setEligibleInfo(null);
    } finally {
      setEligibleLoading(false);
    }
  };

  const fetchEligibleCountForSell = async () => {
    setEligibleLoading(true);
    try {
      // For SELL: pass amount=0 to indicate we only care about who holds the stock
      const data = await copyTradingService.getEligibleFollowers(0, stock.id);
      setEligibleInfo(data);
    } catch {
      setEligibleInfo(null);
    } finally {
      setEligibleLoading(false);
    }
  };

  const formatNum = (n) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  // ── Subtotal = quantity × price ──────────────────────────────────────────────
  const calculateSubtotal = () => {
    if (activeField === 'total') return parseFloat(totalPrice) || 0;
    const qty   = parseFloat(quantity) || 0;
    const price = isMarketOrder
      ? getCurrentStockPrice()
      : (parseFloat(limitPrice) || 0);
    return qty * price;
  };

  // ── Live fee breakdown ───────────────────────────────────────────────────────
  const feeBreakdown = computeFeeBreakdown(orderSide, calculateSubtotal());

  // ── Input handlers ───────────────────────────────────────────────────────────
  const handleQuantityChange = (e) => {
    const val = e.target.value;
    setActiveField('quantity');
    setQuantity(val);
    const qty   = parseFloat(val) || 0;
    const price = isMarketOrder ? getCurrentStockPrice() : (parseFloat(limitPrice) || 0);
    setTotalPrice(qty > 0 && price > 0 ? formatNum(qty * price) : '');
  };

  const handleTotalPriceChange = (e) => {
    const val = e.target.value;
    setActiveField('total');
    setTotalPrice(val);
    const total = parseFloat(val) || 0;
    const price = isMarketOrder ? getCurrentStockPrice() : (parseFloat(limitPrice) || 0);
    if (total > 0 && price > 0) {
      // FIX: compute raw quantity from division, then round to 6dp to match
      // the precision of UserStockHolding.quantity (DecimalField decimal_places=6).
      // Previously toFixed(6) was applied but then parseFloat stripped trailing
      // zeros, leaving a value like 5.5 while the DB stored 5.499999 — causing
      // a mismatch. Now we keep the full 6dp string so the backend receives a
      // value it can normalize to exactly what is stored in the holding.
      const rawQty    = total / price;
      const roundedQty = parseFloat(rawQty.toFixed(6));
      setQuantity(String(roundedQty));
    } else {
      setQuantity('');
    }
  };

  // ── Referral bonus helpers ───────────────────────────────────────────────────
  const canUseReferralBonus = () => {
    if (orderSide !== 'BUY' || !referralWallet) return false;
    return referralWallet.available_balance >= feeBreakdown.grandTotal;
  };

  const getPaymentBreakdown = () => {
    if (useReferralBonus && canUseReferralBonus()) {
      return { referralBonus: feeBreakdown.grandTotal, mainWallet: 0 };
    }
    return { referralBonus: 0, mainWallet: feeBreakdown.grandTotal };
  };

  // ── Order type descriptions ──────────────────────────────────────────────────
  const getOrderTypeDescription = () => {
    if (isMarketOrder) {
      return orderSide === 'BUY'
        ? 'A Market Buy order will execute immediately at the current market price.'
        : 'A Market Sell order will execute immediately at the current market price.';
    }
    return orderSide === 'BUY'
      ? 'A Limit Buy order allows you to set a maximum price. Funds (including fee) are reserved until the order executes or is cancelled.'
      : 'A Limit Sell order allows you to set a minimum price. The order executes when the stock reaches or exceeds your price.';
  };

  // ── Helper: round to 6 decimal places (matching backend precision) ───────────
  const roundTo6Decimals = (value) => {
    return Math.round(value * 1000000) / 1000000;
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!quantity || parseFloat(quantity) <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    if (!isMarketOrder && (!limitPrice || parseFloat(limitPrice) <= 0)) {
      setError('Please enter a valid limit price');
      return;
    }

    // FIX: improved sell quantity validation with 6dp precision and auto-correct.
    // Rounds both the input and the holding to 6dp (matching backend precision)
    // before comparing. If they are within 0.001 of each other (covers rounding
    // differences from total-price back-calculation), auto-corrects to the exact
    // holding amount so the order proceeds cleanly rather than being rejected.
    if (orderSide === 'SELL' && userHolding) {
      const userQty = parseFloat(userHolding.quantity);
      let inputQty = parseFloat(quantity);

      // Round both to 6 decimal places (matching backend precision)
      const roundedUserQty  = roundTo6Decimals(userQty);
      const roundedInputQty = roundTo6Decimals(inputQty);

      // Auto-correct if within 0.001 shares (covers rounding differences from
      // total price back-calculations)
      if (Math.abs(roundedInputQty - roundedUserQty) <= 0.001) {
        const correctedQty = roundedUserQty;
        setQuantity(correctedQty.toString());
        // Recalculate total price after auto-correction
        const price = isMarketOrder ? getCurrentStockPrice() : (parseFloat(limitPrice) || 0);
        setTotalPrice((correctedQty * price).toFixed(2));
        setError(null);
        // Update inputQty for the rest of the validation
        inputQty = correctedQty;
      }

      // Final validation with reasonable tolerance
      const finalInputQty = roundTo6Decimals(inputQty);
      if (finalInputQty > roundedUserQty + 0.001) {
        setError(`You only own ${roundedUserQty.toFixed(6)} shares`);
        return;
      }
    }

    if (orderSide === 'BUY' && portfolioSummary) {
      const { grandTotal } = feeBreakdown;
      const payment = getPaymentBreakdown();

      // Referral must cover the grand total (subtotal + fee)
      if (useReferralBonus && !canUseReferralBonus()) {
        setError(`Insufficient referral bonus. Need ${formatCurrency(grandTotal)} (includes fee), have ${formatCurrency(referralWallet.available_balance)} available.`);
        return;
      }
      if (!useReferralBonus && grandTotal > portfolioSummary.available_balance) {
        setError(`Insufficient balance. You need ${formatCurrency(grandTotal)} (includes fee), but have ${formatCurrency(portfolioSummary.available_balance)} available.`);
        return;
      }
      if (useReferralBonus && payment.mainWallet > portfolioSummary.available_balance) {
        setError(`Insufficient main wallet balance.`);
        return;
      }
    }

    // Copy trade validation
    if (copyTradeEnabled && leaderProfile) {
      if (orderSide === 'BUY') {
        const ctAmount = parseFloat(copyTradeAmount);
        if (!ctAmount || ctAmount <= 0) {
          setError('Please enter a valid copy trade amount, or uncheck "Mirror this trade".');
          return;
        }
      }
      // For SELL: no amount needed, just checkbox is enough
    }

    const orderData = {
      stock_id:           stock.id,
      order_type:         isMarketOrder ? 'MARKET' : 'LIMIT',
      order_side:         orderSide,
      quantity:           parseFloat(quantity),
      use_referral_bonus: useReferralBonus && orderSide === 'BUY',
    };
    if (!isMarketOrder) orderData.limit_price = parseFloat(limitPrice);

    // Attach copy trade data
    if (copyTradeEnabled && leaderProfile) {
      if (orderSide === 'BUY') {
        const ctAmount = parseFloat(copyTradeAmount);
        if (ctAmount > 0) {
          orderData.copy_trade_amount = ctAmount;
        }
      } else {
        // For SELL: send copy_trade_amount = 0 to indicate "sell 100% of holdings"
        orderData.copy_trade_amount = 0;
      }
    }

    // Use the shared useSellLockRetry hook's attemptAction.
    // If the backend returns sell_locked=true, the hook keeps the spinner going
    // and retries every 5 s automatically.  If the modal closes mid-retry the
    // hook cancels the timer — the sell NEVER fires in the background.
    attemptAction(
      () => tradingService.createOrder(orderData),
      (response) => {
        setSuccess(response.message);
        setQuantity(''); setTotalPrice(''); setActiveField(null);
        setLimitPrice(''); setUseReferralBonus(false);
        setCopyTradeAmount(''); setCopyTradeEnabled(false); setEligibleInfo(null);
        fetchUserData();
      },
      (err) => setError(err.message || 'Failed to place order'),
    );
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await tradingService.cancelOrder(orderId);
      setSuccess('Order cancelled successfully');
      fetchUserData();
    } catch (err) {
      setError(err.message || 'Failed to cancel order');
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────────
  const priceChangeColor  = stock.price_change_percentage >= 0 ? '#059669' : '#dc2626';
  const priceChangeSymbol = stock.price_change_percentage >= 0 ? '+' : '';
  const paymentBreakdown  = getPaymentBreakdown();
  const currencySymbol    = getCurrencySymbol();
  const currentPrice      = getCurrentStockPrice();
  const hasAmount         = feeBreakdown.subtotal > 0;

  // Determine copy trade hint text based on order side
  const getCopyTradeHint = () => {
    if (orderSide === 'BUY') {
      return `Set the amount (in ${currencySymbol}) each follower will buy. Followers must have sufficient balance.`;
    }
    return `When checked, ALL followers who hold ${stock.symbol} will sell 100% of their holdings automatically. No amount needed.`;
  };

  const getCopyTradeLabel = () => {
    if (orderSide === 'BUY') {
      return `Copy Trade Amount per Follower (${currencySymbol})`;
    }
    return `Sell 100% for Followers`;
  };

  // Format holding display with proper precision
  const getHoldingDisplay = () => {
    if (!userHolding) return '0 shares';
    const qty = parseFloat(userHolding.quantity);
    return `${qty.toFixed(6)} shares of ${stock.name} @ ${userHolding.average_buy_price_display || formatCurrency(userHolding.average_buy_price)} avg`;
  };

  return (
    <div className="trading-modal-overlay" onClick={onClose}>
      <div className="trading-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="trading-modal-header">
          <div className="stock-info-header">
            <h2>{stock.symbol} - {stock.name}</h2>
            <div className="price-info">
              <span className="current-price">{formatCurrency(currentPrice)}</span>
              <span className="price-change" style={{ color: priceChangeColor }}>
                {priceChangeSymbol}{stock.price_change_percentage}%
              </span>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Order description */}
        <div className="order-description">
          <p>{getOrderTypeDescription()}</p>
        </div>

        {/* User info bar */}
        <div className="user-info-bar">
          <div className="wallet-info">
            <span className="label">Main Balance:</span>
            <span className="value">
              {portfolioSummary ? (
                <>
                  {portfolioSummary.wallet_balance_display || formatCurrency(portfolioSummary.wallet_balance)}
                  <span className="available">
                    {' '}({portfolioSummary.available_balance_display || formatCurrency(portfolioSummary.available_balance)} available)
                  </span>
                </>
              ) : 'Loading...'}
            </span>
          </div>
          {referralWallet && referralWallet.available_balance > 0 && orderSide === 'BUY' && (
            <div className="wallet-info">
              <span className="label">Referral Bonus:</span>
              <span className="value">
                {portfolioSummary?.referral_wallet_available_display || formatCurrency(referralWallet.available_balance)} available
              </span>
            </div>
          )}
          <div className="holding-info">
            <span className="label">You own:</span>
            <span className="value">
              {getHoldingDisplay()}
            </span>
          </div>
        </div>

        {/* Order type tabs */}
        <div className="order-type-tabs">
          <button className={`tab ${isMarketOrder ? 'active' : ''}`}  onClick={() => setIsMarketOrder(true)}>
            Market Order
          </button>
          <button className={`tab ${!isMarketOrder ? 'active' : ''}`} onClick={() => setIsMarketOrder(false)}>
            Limit Order
          </button>
        </div>

        {/* Buy / Sell toggle */}
        <div className="order-side-toggle">
          <button className={`side-button buy ${orderSide === 'BUY'  ? 'active' : ''}`} onClick={() => setOrderSide('BUY')}>BUY</button>
          <button className={`side-button sell ${orderSide === 'SELL' ? 'active' : ''}`} onClick={() => setOrderSide('SELL')}>SELL</button>
        </div>

        {/* Order form */}
        <form onSubmit={handleSubmit} className="order-form">
          <div className="form-row">
            <div className="form-group">
              <label>Number of Asset</label>
              <input
                type="number" step="0.000001"
                value={quantity}
                onChange={handleQuantityChange}
                placeholder="Enter shares"
                disabled={isLoading}
              />
              {orderSide === 'SELL' && userHolding && (
                <span className="helper-text">Max: {parseFloat(userHolding.quantity).toFixed(6)} shares</span>
              )}
            </div>

            <div className="form-group">
              <label>Trade Value ({currencySymbol})</label>
              <input
                type="number" step="0.01"
                value={totalPrice}
                onChange={handleTotalPriceChange}
                placeholder="Enter total amount"
                disabled={isLoading}
                className="total-price-input"
              />
            </div>
          </div>

          {!isMarketOrder && (
            <div className="form-group">
              <label>Limit Price</label>
              <input
                type="number" step="0.01"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                placeholder="Enter limit price"
                disabled={isLoading}
              />
              <span className="helper-text">
                {orderSide === 'BUY'
                  ? 'Buy when price reaches or goes below this'
                  : 'Sell when price reaches or goes above this'}
              </span>
            </div>
          )}

          {isMarketOrder && (
            <div className="price-display">
              <span className="label">Price per Share:</span>
              <span className="value">{formatCurrency(currentPrice)}</span>
            </div>
          )}

          {/* ── Fee Breakdown (shown as soon as user enters an amount) ── */}
          {hasAmount && (
            <div className="fee-breakdown-box">
              <div className="fee-breakdown-row">
                <span>Trade Value</span>
                <span>{formatCurrency(feeBreakdown.subtotal)}</span>
              </div>
              <div className="fee-breakdown-row fee-row">
                <span>
                  {orderSide === 'BUY' ? 'Buying Fee (0.5%)' : 'Selling Fee (1.0%)'}
                </span>
                <span className="fee-amount">−{formatCurrency(feeBreakdown.fee)}</span>
              </div>
              <div className="fee-breakdown-row grand-total-row">
                <span>
                  {orderSide === 'BUY' ? 'Total You Pay' : 'Total You Receive'}
                </span>
                <span className={orderSide === 'BUY' ? 'total-pay' : 'total-receive'}>
                  {formatCurrency(feeBreakdown.grandTotal)}
                </span>
              </div>
              {!isMarketOrder && orderSide === 'BUY' && (
                <div className="fee-note">
                  ℹ️ Estimated based on your limit price. Fee is settled at the actual fill price.
                </div>
              )}
            </div>
          )}

          {/* Referral bonus option */}
          {orderSide === 'BUY' && referralWallet && referralWallet.available_balance > 0 && (
            <div className="referral-bonus-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={useReferralBonus}
                  onChange={(e) => setUseReferralBonus(e.target.checked)}
                  disabled={isLoading || !canUseReferralBonus()}
                />
                <span>Use Referral Bonus for this trade</span>
              </label>
              <div className="bonus-info">
                Available: {formatCurrency(referralWallet.available_balance)}
                {!canUseReferralBonus() && useReferralBonus && (
                  <span className="insufficient-warning">
                    &nbsp;(Insufficient bonus funds — must cover trade + fee)
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Payment breakdown (referral) */}
          {orderSide === 'BUY' && hasAmount && (
            <div className="payment-breakdown">
              <h4>Payment Source</h4>
              {useReferralBonus && canUseReferralBonus() ? (
                <div className="breakdown-item referral">
                  <span>Referral Bonus:</span>
                  <span>{formatCurrency(paymentBreakdown.referralBonus)}</span>
                </div>
              ) : (
                <div className="breakdown-item main">
                  <span>Main Wallet:</span>
                  <span>{formatCurrency(paymentBreakdown.mainWallet)}</span>
                </div>
              )}
            </div>
          )}

          {!isMarketOrder && orderSide === 'BUY' && hasAmount && (
            <div className="info-message">
              {formatCurrency(feeBreakdown.grandTotal)} (trade + fee) will be reserved from your{' '}
              {useReferralBonus ? 'referral bonus' : 'available balance'}
            </div>
          )}

          {/* ── Copy Trade Section ────────────────────────────────────────────── */}
          {/* Shown to leaders with active followers on both BUY and SELL orders */}
          {leaderProfile && (
            <div className="copy-trade-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={copyTradeEnabled}
                  onChange={(e) => {
                    setCopyTradeEnabled(e.target.checked);
                    if (!e.target.checked) {
                      setCopyTradeAmount('');
                      setEligibleInfo(null);
                    }
                  }}
                  disabled={isLoading}
                />
                <span>Mirror this trade to my followers</span>
              </label>
              <div className="copy-trade-hint">
                You have <strong>{leaderProfile.active_followers_count}</strong> active follower{leaderProfile.active_followers_count !== 1 ? 's' : ''}.
                {' '}{getCopyTradeHint()}
              </div>

              {copyTradeEnabled && (
                <div className="copy-trade-amount-wrap">
                  {/* Only show amount input for BUY orders */}
                  {orderSide === 'BUY' ? (
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label>{getCopyTradeLabel()}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={copyTradeAmount}
                        onChange={(e) => setCopyTradeAmount(e.target.value)}
                        placeholder={`Amount in ${currencySymbol}`}
                        disabled={isLoading}
                        className="ct-amount-input"
                      />
                      <span className="helper-text">
                        Each follower with enough balance will buy exactly this amount of {stock.symbol}.
                      </span>
                    </div>
                  ) : (
                    /* For SELL: show info message instead of input */
                    <div className="sell-full-liquidation-info" style={{ 
                      padding: '10px 14px', 
                      background: '#e6f7ef', 
                      borderRadius: '8px', 
                      fontSize: '0.85rem',
                      color: '#065f46',
                      marginTop: '8px'
                    }}>
                      🔄 When you sell, ALL your followers who hold {stock.symbol} will automatically sell 100% of their position in this asset.
                    </div>
                  )}

                  {/* Eligible followers counter - shows differently for BUY vs SELL */}
                  {orderSide === 'BUY' && copyTradeAmount && parseFloat(copyTradeAmount) > 0 && (
                    <div className={`ct-eligible-badge ${
                      eligibleLoading ? 'loading' :
                      eligibleInfo?.eligible > 0 ? 'has-eligible' : 'none-eligible'
                    }`}>
                      {eligibleLoading ? (
                        '⏳ Checking followers...'
                      ) : eligibleInfo ? (
                        <>
                          <span className="ct-eligible-count">
                            {eligibleInfo.eligible} of {eligibleInfo.total}
                          </span>
                          {' '}follower{eligibleInfo.total !== 1 ? 's' : ''} have sufficient balance
                          {eligibleInfo.eligible > 0 && eligibleInfo.amount_display && (
                            <span className="ct-eligible-amount">
                              {' '}({eligibleInfo.amount_display} each)
                            </span>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}

                  {/* For SELL: show who holds the stock */}
                  {orderSide === 'SELL' && (
                    <div className={`ct-eligible-badge ${
                      eligibleLoading ? 'loading' :
                      eligibleInfo?.eligible > 0 ? 'has-eligible' : 'none-eligible'
                    }`}>
                      {eligibleLoading ? (
                        '⏳ Checking followers...'
                      ) : eligibleInfo ? (
                        <>
                          <span className="ct-eligible-count">
                            {eligibleInfo.eligible} of {eligibleInfo.total}
                          </span>
                          {' '}follower{eligibleInfo.total !== 1 ? 's' : ''} currently hold {stock.symbol} and will sell 100%
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── End Copy Trade Section ─────────────────────────────────────────── */}

          {error   && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <button
            type="submit"
            className={`submit-button ${orderSide.toLowerCase()}`}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : (
              isMarketOrder
                ? `${orderSide === 'BUY' ? 'Buy' : 'Sell'} ${quantity ? parseFloat(quantity).toFixed(2) : '0'} Assets`
                : `Place ${orderSide === 'BUY' ? 'Buy' : 'Sell'} Limit Order`
            )}
          </button>
        </form>

        {/* Pending orders */}
        {pendingOrders.length > 0 && (
          <div className="additional-section">
            <button className="section-toggle" onClick={() => setShowPendingOrders(!showPendingOrders)}>
              <span>Pending Orders ({pendingOrders.length})</span>
              <span>{showPendingOrders ? '▲' : '▼'}</span>
            </button>
            {showPendingOrders && (
              <div className="section-content">
                {pendingOrders.map(order => (
                  <div key={order.id} className="order-item">
                    <div className="order-info">
                      <span className={`order-side ${order.order_side.toLowerCase()}`}>{order.order_side}</span>
                      <span>
                        {parseFloat(order.quantity).toFixed(2)} shares @{' '}
                        {order.limit_price_display || formatCurrency(order.limit_price || order.price)}
                      </span>
                      <span className="order-type">{order.order_type}</span>
                      {order.fee_display && (
                        <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                          Fee: {order.fee_display}
                        </span>
                      )}
                    </div>
                    <button className="cancel-button" onClick={() => handleCancelOrder(order.id)}>
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent transactions */}
        {recentTransactions.length > 0 && (
          <div className="additional-section">
            <button className="section-toggle" onClick={() => setShowTransactions(!showTransactions)}>
              <span>Recent Transactions ({recentTransactions.length})</span>
              <span>{showTransactions ? '▲' : '▼'}</span>
            </button>
            {showTransactions && (
              <div className="section-content">
                {recentTransactions.map(transaction => (
                  <div key={transaction.id} className="transaction-item">
                    <div className="transaction-main">
                      <span className={`transaction-type ${transaction.transaction_type.toLowerCase()}`}>
                        {transaction.transaction_type}
                      </span>
                      <span className="transaction-detail">
                        {parseFloat(transaction.quantity).toFixed(2)} shares of {transaction.stock_name || stock.name} @{' '}
                        {transaction.price_per_share_display || formatCurrency(transaction.price_per_share)}
                        {transaction.fee_display && (
                          <span style={{ fontSize: '0.75rem', color: '#dc2626', marginLeft: 6 }}>
                            Fee: {transaction.fee_display}
                          </span>
                        )}
                      </span>
                      <span className="transaction-date">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {transaction.transaction_type === 'SELL' && transaction.profit_loss_display && (
                      <div className={`transaction-pnl ${parseFloat(transaction.profit_loss) >= 0 ? 'profit' : 'loss'}`}>
                        {parseFloat(transaction.profit_loss) >= 0 ? 'Profit: +' : 'Loss: '}
                        {transaction.profit_loss_display}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default TradingModal;